import { useEffect, useRef } from 'react';

/**
 * Hook: arm a full-bleed video to play only on the presenter's SECOND
 * click on its slide.
 *
 * Behaviour:
 *  • Slide enters viewport → video stays paused at time 0. NO autoplay.
 *  • 1st non-interactive click on the slide → video starts. The click
 *    is swallowed (stopImmediatePropagation) so the presentation does
 *    NOT advance.
 *  • 2nd click → normal slide advance handled by SmoothScroll.
 *  • Slide leaves viewport → video pauses + rewinds; arming resets.
 *
 * @param {{ stageId?: string }} opts — sectionId used to detect visibility
 * @returns refs { videoRef, stageRef } to attach to <div> and <video>.
 */
export default function useClickToArmVideo(sectionId) {
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const insideRef = useRef(false);
  const armedRef = useRef(false);

  useEffect(() => {
    const stage = stageRef.current;
    const v = videoRef.current;
    if (!stage || !v) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          insideRef.current = true;
        } else {
          insideRef.current = false;
          armedRef.current = false;
          try { v.pause(); v.currentTime = 0; } catch (e) { /* noop */ }
        }
      },
      { threshold: [0, 0.25, 0.6, 1] },
    );
    io.observe(stage);

    const isInteractivePath = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      return path.some((node) => {
        if (!node || node.nodeType !== 1) return false;
        return typeof node.matches === 'function'
          && node.matches('a, button, input, textarea, select, label, [role="button"], [data-no-advance]');
      });
    };

    const onArm = (e) => {
      if (!insideRef.current) return;
      if (armedRef.current) return;              // already armed → allow advance
      if (isInteractivePath(e)) return;          // click on controls → let through
      armedRef.current = true;
      try { v.currentTime = 0; } catch (err) { /* noop */ }
      // Fire play() synchronously inside the user-gesture click handler
      // so browsers accept it. Do NOT preventDefault here — that can
      // invalidate the user-activation on some engines.
      const p = v.play();
      if (p && typeof p.then === 'function') p.catch(() => {});
      e.stopImmediatePropagation();
    };

    const onArmKey = (e) => {
      if (!insideRef.current) return;
      if (armedRef.current) return;
      if (!['ArrowDown', 'ArrowRight', 'PageDown', ' '].includes(e.key)) return;
      armedRef.current = true;
      try { v.currentTime = 0; } catch (err) { /* noop */ }
      const p = v.play();
      if (p && typeof p.then === 'function') p.catch(() => {});
      e.stopImmediatePropagation();
      e.preventDefault();
    };

    window.addEventListener('click', onArm, { capture: true });
    window.addEventListener('keydown', onArmKey, { capture: true });

    return () => {
      io.disconnect();
      window.removeEventListener('click', onArm, { capture: true });
      window.removeEventListener('keydown', onArmKey, { capture: true });
      try { v.pause(); } catch (err) { /* noop */ }
    };
  }, [sectionId]);

  return { videoRef, stageRef };
}
